<script setup lang="ts">
import type { Menu } from "@mmh/types"
import { useHead } from "@unhead/vue"
import { computed, nextTick, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router"
import { ContactDialog } from "~/components/contact"
import { ThemeDivider } from "~/components/theme"
import { useBottomBarVisibility } from "~/composables/bottomBar"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { titleCase } from "~/lib/case"
import { buildRestaurantSchema } from "~/lib/restaurantSchema"
import { cn } from "~/lib/utils"
import AboutSection from "./components/AboutSection.vue"
import CateringSection from "./components/CateringSection.vue"
import FAQSection from "./components/FAQSection.vue"
import FeaturedSection from "./components/FeaturedSection.vue"
import GallerySection from "./components/GallerySection.vue"
import GiftCardsRewardsSection from "./components/GiftCardsRewardsSection.vue"
import HeroSection from "./components/HeroSection.vue"
import HospitalityGroupSection from "./components/HospitalityGroupSection.vue"
import HoursLocationSection from "./components/HoursLocationSection.vue"
import LargePartyReservationsSection from "./components/LargePartyReservationsSection.vue"
import MenuSection from "./components/MenuSection.vue"
import MerchSection from "./components/MerchSection.vue"
import PrivateDiningSection from "./components/PrivateDiningSection.vue"

type SectionDef = {
    component: unknown
    id: string
    dataKey: string
    class?: string
    divider?: boolean
    show?: (key: string) => boolean
}

type SectionCopy = {
    title: string
    h1?: string
    description: string
    fromCms?: boolean
}

const { concept } = useConcept()
const route = useRoute()
const router = useRouter()
const { location, hasOneLocation, locationUrlPath, menuPath, absoluteUrl, reservationsPath } = useLocation()

useBottomBarVisibility()

const contactOpen = computed(() => route.meta.section === "contact")

function onContactOpenChange(value: boolean) {
    if (value) return
    if (window.history.state?.back) {
        router.back()
    } else {
        router.replace(hasOneLocation.value ? "/" : `/${route.params.name}`)
    }
}

const section = computed(() => route.meta.section as string | undefined)

const menuCategory = computed(() =>
    section.value === "menus" ? (route.params.category as string | undefined) : undefined
)

const cuisine = computed(() => concept.value.cuisine || "Italian")

const brandName = computed(() => {
    const name = titleCase(concept.value.name)
    return !hasOneLocation.value && location.value?.name
        ? `${name} ${titleCase(location.value.name)}`
        : name
})

const placeName = computed(() => {
    if (location.value?.searchPlace) return location.value.searchPlace
    const { city, state } = location.value?.address ?? {}
    if (!city) return ""
    return state ? `${city}, ${state}` : city
})

const metaDescriptionText = computed(() =>
    location.value?.metaDescription
    || concept.value.metaDescription
    || location.value?.tagline
    || concept.value.tagline
)

const META_DESCRIPTION_LIMIT = 155

function toPlainText(html?: string) {
    return html?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() ?? ""
}

function toMetaDescription(html?: string, limit = META_DESCRIPTION_LIMIT) {
    const text = toPlainText(html)
    if (text.length <= limit) return text

    const cut = text.slice(0, limit)
    const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "))

    // Prefer a clean sentence break; otherwise drop the dangling partial word.
    return (stop > 80 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, "")).trim()
}

const sectionCopy = computed<Record<string, SectionCopy>>(() => ({
    "menus": {
        title: "Menus",
        h1: `${cuisine.value} Menus`,
        description: `Explore our seasonal ${cuisine.value} menus`
    },
    "large-party-reservations": {
        title: "Large Party Reservations",
        description: "Book for parties of 7 or more. Prix fixe menus available for groups"
    },
    "private-dining": {
        title: "Private Dining",
        h1: "Private Dining & Events",
        description: "Host your special occasion or corporate event in our private and semi-private dining spaces"
    },
    "catering": {
        title: "Catering",
        h1: `${cuisine.value} Catering`,
        description: `Order ${cuisine.value} catering for your next event. Pickup and delivery available`
    },
    "merch": {
        title: "Merch",
        description: "Browse the MMH Collection — premium everyday essentials inspired by our restaurants"
    },
    "hours-location": {
        title: "Hours & Location",
        description: "Find our hours, address, and contact information. Indoor and outdoor seating available"
    },
    "faq": {
        title: "FAQs",
        h1: "Frequently Asked Questions",
        description: "Answers to common questions about reservations, dining policies, and more"
    },
    "gift-cards-rewards": {
        title: "Gift Cards & Rewards",
        description: "Purchase gift cards or join our rewards program to earn points on every visit"
    },
    "contact": {
        title: "Contact Us",
        description: "Get in touch with us"
    }
}))

const menuCategoryCopy = computed<Record<string, SectionCopy>>(() => ({
    "daily": {
        title: "Daily Menu",
        description: `Our everyday ${cuisine.value} menu — antipasti, pasta, pizza, and mains`
    },
    "special": {
        title: "Specials Menu",
        description: "Seasonal specials and limited-time dishes, updated regularly"
    },
    "large-party": {
        title: "Large Party Menu",
        description: "Family-style prix fixe menus for large groups and private events"
    },
    "catering": {
        title: "Catering Menu",
        description: `${cuisine.value} catering trays and packages for pickup or delivery`
    }
}))

const CMS_COPY_KEYS: Record<string, string> = {
    "large-party-reservations": "largePartyReservations",
    "private-dining": "privateDining"
}

const currentCopy = computed<SectionCopy | undefined>(() => {
    if (!section.value) return undefined

    if (menuCategory.value) {
        const categoryCopy = menuCategoryCopy.value[menuCategory.value]
        if (categoryCopy) return categoryCopy
    }

    const fallback = sectionCopy.value[section.value]
    if (!fallback) return undefined

    const cmsKey = CMS_COPY_KEYS[section.value]
    const cmsBlock = cmsKey ? (location.value?.pageContent as any)?.[cmsKey] : undefined
    if (!cmsBlock) return fallback

    const authored = cmsBlock.metaDescription?.trim()
    const description = authored || toMetaDescription(cmsBlock.content)
    if (!description) return fallback

    return {
        ...fallback,
        title: cmsBlock.title || fallback.title,
        description,
        fromCms: true
    }
})

const pageUrl = computed(() => {
    let url = concept.value.website
    if (location.value?.path && !hasOneLocation.value) url += `/${location.value.path}`
    if (section.value) url += `/${section.value}`
    if (menuCategory.value) url += `/${menuCategory.value}`
    return url
})

const metaImage = computed(() => {
    const path = location.value?.ogImage || concept.value.images?.ogImage
    if (!path) return undefined
    return concept.value.website + path
})

const metaTitle = computed(() => {
    if (currentCopy.value) return `${currentCopy.value.title} | ${brandName.value}`
    if (location.value) {
        const tagline = location.value.tagline || concept.value.tagline
        return `${brandName.value} | ${tagline}`
    }
    return brandName.value
})

const metaDescription = computed(() => {
    const copy = currentCopy.value
    if (!copy) return metaDescriptionText.value || ""

    if (copy.fromCms) return copy.description

    return `${copy.description} at ${brandName.value}`
})

const pageH1 = computed(() => {
    if (currentCopy.value) {
        return `${currentCopy.value.h1 || currentCopy.value.title} at ${brandName.value}`
    }
    const suffix = placeName.value
        ? `${cuisine.value} Restaurant in ${placeName.value}`
        : `${cuisine.value} Restaurant`
    return `${brandName.value} — ${suffix}`
})

const locationUrl = computed(() => absoluteUrl(locationUrlPath()))

const restaurantSchema = computed(() => {
    const loc = location.value
    if (!loc) return null

    return buildRestaurantSchema({
        name: brandName.value,
        image: metaImage.value,
        url: locationUrl.value,
        location: loc,
        cuisine: cuisine.value,
        socialMedia: concept.value.socialMedia,
        menuUrl: absoluteUrl(menuPath()),
        reservationsUrl: absoluteUrl(reservationsPath())
    })
})

const faqSchema = computed(() => {
    if (section.value !== "faq") return null

    const faqs = location.value?.pageContent?.faqs
    const items = Array.isArray(faqs) ? faqs : (faqs as any)?.items || []
    if (!items.length) return null

    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map((faq: { question: string; answer: string }) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": toPlainText(faq.answer)
            }
        }))
    }
})

useHead({
    title: metaTitle,
    meta: [
        { name: "description", content: metaDescription },
        { property: "og:title", content: metaTitle },
        { property: "og:site_name", content: brandName },
        { property: "og:description", content: metaDescription },
        { property: "og:url", content: pageUrl },
        { property: "og:type", content: "website" },
        { property: "og:image", content: metaImage },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
        { name: "twitter:image", content: metaImage }
    ],
    link: [{ rel: "canonical", href: pageUrl }],
    script: computed(() =>
        [restaurantSchema.value, faqSchema.value]
            .filter(Boolean)
            .map(data => ({ type: "application/ld+json", innerHTML: JSON.stringify(data) }))
    )
})

const pageContent = computed(() => (location.value?.pageContent ?? {}) as Record<string, any>)

const notHidden = (key: string) => pageContent.value[key]?.visible !== false

const hasItems = (key: string) => {
    const data = pageContent.value[key]
    if (Array.isArray(data)) return data.length > 0
    return data?.visible !== false && data?.items?.length > 0
}

const exists = (key: string) => !!pageContent.value[key]

const defaultSectionClass = "w-full max-w-5xl mx-auto px-4 md:px-6 py-20 md:py-32"

const allSections: SectionDef[] = [
    { component: HeroSection, id: "hero", dataKey: "hero", class: "h-[95svh]", divider: false },
    { component: AboutSection, id: "about", dataKey: "about" },
    { component: FeaturedSection, id: "featured", dataKey: "featuredSection", show: notHidden },
    { component: GallerySection, id: "gallery", dataKey: "gallery", show: hasItems },
    {
        component: MenuSection,
        id: "menus",
        dataKey: "menus",
        show: key =>
            notHidden(key) &&
            (Object.values(location.value?.menus || {}) as Menu[][]).some(arr => arr.length > 0)
    },
    {
        component: LargePartyReservationsSection,
        id: "large-party-reservations",
        dataKey: "largePartyReservations",
        show: notHidden
    },
    { component: PrivateDiningSection, id: "private-dining", dataKey: "privateDining", show: notHidden },
    { component: CateringSection, id: "catering", dataKey: "catering", show: notHidden },
    {
        component: MerchSection,
        id: "merch",
        dataKey: "merch",
        show: () => !!concept.value?.merch
    },
    { component: HoursLocationSection, id: "hours-location", dataKey: "hoursLocationContact", show: notHidden },
    {
        component: HospitalityGroupSection,
        id: "hospitality-group",
        dataKey: "hospitalityGroup",
        class: "w-full max-w-6xl mx-auto px-4 md:px-6 py-20 md:py-32",
        show: notHidden
    },
    { component: GiftCardsRewardsSection, id: "gift-cards-rewards", dataKey: "giftCardsRewards", show: notHidden },
    { component: FAQSection, id: "faq", dataKey: "faqs", divider: false, show: hasItems }
]

const sections = computed(() => {
    if (!location.value) return []

    const candidates =
        import.meta.env.SSR && section.value
            ? allSections.filter(s => s.id === section.value || s.id === "hero")
            : allSections

    return candidates.filter(s => (s.show ?? exists)(s.dataKey))
})

onMounted(async () => {
    await nextTick()

    const scrollTarget = (route.meta.scrollTo as string) || route.hash
    const el = scrollTarget && document.querySelector(scrollTarget)

    if (el) {
        el.scrollIntoView({ behavior: "instant", block: "start" })
    } else {
        window.scrollTo({ top: 0, behavior: "instant" })
    }

    document.getElementById("nav-topbar")?.focus({ preventScroll: true })
})
</script>

<template>
    <div class="w-full overflow-hidden flex flex-col">
        <h1 class="sr-only">
            {{ pageH1 }}
        </h1>
        <template
            v-for="section of sections"
            :key="`${location?.name}-${section.id}`"
        >
            <Component
                :is="section.component"
                :id="section.id"
                :class="cn(section.class ?? defaultSectionClass)"
            />
            <ThemeDivider v-if="section.divider !== false" />
        </template>
        <ContactDialog
            :open="contactOpen"
            @update:open="onContactOpenChange"
        />
    </div>
</template>